import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button } from 'react-bootstrap';
import { sortBy } from 'lodash';

import { ApiServiceServerless } from '../xhr_libs';
import AppContext from '../contexts/app-context';
import EnergyTracerTable, { AddEntityFilterHeader } from './tables';
import { SetPortfolioLinkFormatter } from './tables/formatters';
import PortfolioModal from '../components/modals/PortfolioModal';
import { textFilterIncludes } from '../components/tables/filters';
import {
  portfolioOrganizationFormatter,
  portfolioOrganizationFilter,
} from './tables/formatters/portfolioTable';
import Loader from '../components/Loader';

export default function PortfolioTable() {
  const { setActivePortfolio, showToast } = useContext(AppContext);

  const [portfolios, setPortfolios] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [organizationFilterValue, setOrganizationFilterValue] = useState('');

  const getPortfolios = useCallback(() => {
    setIsLoading(true);
    ApiServiceServerless.get('/portfolios')
      .then((res) => {
        setPortfolios(res.data);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    getPortfolios();
  }, [getPortfolios]);

  const createTableData = (portfolios) => {
    const tableData = [];
    portfolios = sortBy(portfolios, ['name']);

    portfolios.forEach((portfolio) => {
      const portfolio_orgs = sortBy(portfolio.organizations, ['name']);
      let portfolio_row = {
        id: portfolio.id,
        name: portfolio.name,
        organizations: [],
      };

      portfolio_orgs.forEach((org, idx) => {
        portfolio_row['organizations'].push({ id: org.id, name: org.name });
      });

      tableData.push(portfolio_row);
    });
    return tableData;
  };

  const columnStyle = (row, bordered = false) => {
    const styles = {
      row: {
        borderBottomWidth: 0,
        borderTopWidth: 0,
      },
      firstOrgRow: {
        borderTopWidth: 1,
      },
    };
    let style = bordered ? {} : styles.row;
    if (row['name']) style = { ...style, ...styles.firstOrgRow };
    return style;
  };

  const tableCols = [
    {
      dataField: 'name',
      text: 'Name',
      formatter: SetPortfolioLinkFormatter((row) => [
        `/portfolio/` + row.id,
        { id: row.id, name: row.name },
        setActivePortfolio,
      ]),
      filter: textFilterIncludes('Name', 'name'),
      filterRenderer: (onFilter) => (
        <AddEntityFilterHeader
          onFilter={onFilter}
          filterText='Filter portfolio'
        />
      ),
      style: (_cell, row) => columnStyle(row),
    },
    {
      dataField: 'organizations',
      text: 'Organizations',
      formatExtraData: organizationFilterValue,
      formatter: (cell, row, rowIndex, formatExtraData) =>
        portfolioOrganizationFormatter(row, formatExtraData),
      filter: portfolioOrganizationFilter(setOrganizationFilterValue),
    },
  ];

  const handleRefreshTable = useCallback(() => {
    const tableData = createTableData(portfolios);
    setTableData(tableData);
  }, [portfolios]);

  useEffect(() => {
    handleRefreshTable();
  }, [handleRefreshTable]);

  return (
    <>
      <div style={{ marginBottom: '16px', textAlign: 'right' }}>
        <Button
          variant='success'
          className='ml-3'
          style={{ display: 'inline' }}
          onClick={() => setShowPortfolioModal(true)}
        >
          {' '}
          New Portfolio{' '}
        </Button>
      </div>
      {isLoading ? (
        <Loader style={{ marginTop: '8vh' }} />
      ) : (
        <>
          {tableData.length === 0 ? (
            'No Portfolios Available'
          ) : (
            <EnergyTracerTable
              keyField='id'
              data={tableData}
              columns={tableCols}
              enableFilter={true}
            />
          )}
        </>
      )}

      <PortfolioModal
        setPortfolios={setPortfolios}
        showToast={showToast}
        show={showPortfolioModal}
        onHide={() => setShowPortfolioModal(false)}
      />
    </>
  );
}
